// CrowdDirector (§13.5): the murmur bed follows excitement; reactions are one-shots with their own
// rate limits ("OOOH" at most once per 1.5 s). The crowd surrounds the arena: the murmur loop plays
// as two out-of-phase copies on either side, and each reaction plays both of its rendered variants,
// one per side, a few milliseconds apart (decorrelated, so it is wide without comb filtering).
import { ModeId, RoomPhase } from '@shared/ids';
import type { FrameView } from '../../game/FrameView';
import type { SfxPlayer, Voice } from '../player';
import { bufferGain } from '../prerender';
import { soundDef, type CrowdKey } from '../sounds';

export const OOH_COOLDOWN_S = 1.5;
/** The murmur's level at zero excitement (it rises to 1 at full excitement). */
export const MURMUR_FLOOR = 0.35;
const REACTION_COOLDOWN_S: Readonly<Partial<Record<CrowdKey, number>>> = { 'crowd.ooh': OOH_COOLDOWN_S, 'crowd.gasp': 1, 'crowd.laugh': 1.2, 'crowd.aah': 0.4, 'crowd.cheer': 0.8 };
const LIVE_PHASES: readonly RoomPhase[] = [RoomPhase.INTRO, RoomPhase.PLAY, RoomPhase.ROUND_END];
/** Pan of the two sides of the crowd. */
export const CROWD_SPREAD = 0.7;
/** Two decorrelated copies sum in power: each plays at 1/√2. */
const SIDE = Math.SQRT1_2;

export class CrowdDirector {
  private murmur: Voice | null = null;
  private murmurR: Voice | null = null;
  private readonly last = new Map<CrowdKey, number>();
  private level = 0;
  private flip = false;
  private seed = 0x2545f491;
  private sent = -1;
  enabled = true;

  constructor(private readonly getPlayer: () => SfxPlayer | null, private readonly now: () => number, private readonly caption: (text: string) => void) {}

  /** Murmur gain = 0.35 + 0.65 × excitement (smoothed), only while a round is live. */
  update(view: FrameView, menu: boolean): void {
    const scoring = view.modeId === ModeId.ROYALE || view.modeId === ModeId.HOT_POTATO || view.modeId === ModeId.CROWN_GRAB;
    const live = this.enabled && !menu && LIVE_PHASES.includes(view.roomPhase) && scoring;
    const player = this.getPlayer();
    if (live && !this.murmur && player?.ready('crowd.murmur')) {
      this.murmur = player.play('crowd.murmur', { gain: 0.0001, loop: true, exact: true, pan: -CROWD_SPREAD });
      const half = this.murmur ? (this.murmur.src.buffer?.duration ?? 0) / 2 : 0;
      this.murmurR = player.play('crowd.murmur', { gain: 0.0001, loop: true, exact: true, pan: CROWD_SPREAD, offset: half });
      this.level = 0;
      this.sent = -1;
    }
    if (!live && this.murmur) {
      this.murmur.stop(1.0);
      this.murmurR?.stop(1.0);
      this.murmur = null;
      this.murmurR = null;
    }
    if (this.murmur) {
      const target = MURMUR_FLOOR + (1 - MURMUR_FLOOR) * Math.min(1, Math.max(0, view.excitement));
      const k = 1 - Math.exp(-Math.max(0, view.dt) / 0.3);
      this.level += (target - this.level) * k;
      // AudioParam automation is not free: only send meaningful changes.
      if (Math.abs(this.level - this.sent) > 0.01) {
        this.sent = this.level;
        const t = this.murmur.src.context.currentTime;
        for (const v of [this.murmur, this.murmurR]) {
          if (!v) continue;
          v.gain.gain.setTargetAtTime(this.level * SIDE * (v.src.buffer ? bufferGain(v.src.buffer) : 1), t, 0.1);
          v.src.playbackRate.setTargetAtTime(1 + 0.05 * view.excitement, t, 0.3);
        }
      }
    }
  }

  /** A reaction one-shot, rate-limited per kind. Returns false when skipped. */
  react(key: CrowdKey, gain = 1, captionText?: string): boolean {
    const now = this.now();
    const cd = REACTION_COOLDOWN_S[key] ?? 0.5;
    if (now - (this.last.get(key) ?? -Infinity) < cd) return false;
    this.last.set(key, now);
    const text = captionText ?? soundDef(key).caption;
    if (text) this.caption(text);
    if (!this.enabled) return true;
    const player = this.getPlayer();
    if (!player) return true;
    // Both sides of the stands, a few milliseconds apart (each rendered variant on its own side).
    const side = this.flip ? 1 : -1;
    this.flip = !this.flip;
    const first = player.play(key, { gain: gain * SIDE, pan: side * CROWD_SPREAD, variant: 0 });
    const at = first ? first.start + 0.012 + 0.02 * this.jitter() : undefined;
    player.play(key, { gain: gain * SIDE, pan: -side * CROWD_SPREAD, variant: 1, when: at });
    return true;
  }

  /** One unison clap of the stands at context time `when` (both sides, a hair apart). */
  clap(when: number, gain: number): void {
    const player = this.getPlayer();
    if (!this.enabled || !player) return;
    player.play('crowd.clap', { gain: gain * SIDE, pan: -CROWD_SPREAD, variant: 0, when });
    player.play('crowd.clap', { gain: gain * SIDE, pan: CROWD_SPREAD, variant: 1, when: when + 0.008 + 0.01 * this.jitter() });
  }

  /** Deterministic 0..1 jitter for the side delay. */
  private jitter(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  get murmurLevel(): number {
    return this.murmur ? this.level : 0;
  }
}
